// Email validation
export const validateEmail = (email: string): { valid: boolean; error?: string } => {
  if (!email.trim()) {
    return { valid: false, error: 'Email is required' };
  }
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return { valid: false, error: 'Please enter a valid email address' };
  }
  
  return { valid: true };
};

// Username validation
export const validateUsername = (username: string): { valid: boolean; error?: string } => {
  if (!username.trim()) {
    return { valid: false, error: 'Username is required' };
  }
  
  if (username.length < 3) {
    return { valid: false, error: 'Username must be at least 3 characters long' };
  }
  
  if (username.length > 50) {
    return { valid: false, error: 'Username must be less than 50 characters' };
  }
  
  if (!/^[a-zA-Z0-9_]+$/.test(username)) {
    return { valid: false, error: 'Username can only contain letters, numbers, and underscores' };
  }
  
  return { valid: true };
};

// Password validation with strength calculation
export const validatePassword = (password: string): { 
  valid: boolean; 
  errors: string[]; 
  strength: number; 
  strengthText: string;
  requirements: { met: boolean; text: string }[];
} => {
  const errors: string[] = [];
  let strength = 0;
  const requirements = [
    { met: password.length >= 8, text: 'At least 8 characters' },
    { met: /[A-Z]/.test(password), text: 'One uppercase letter' },
    { met: /[a-z]/.test(password), text: 'One lowercase letter' },
    { met: /[0-9]/.test(password), text: 'One number' },
    { met: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password), text: 'One special character' }
  ];

  // Check requirements
  requirements.forEach(req => {
    if (!req.met) {
      errors.push(req.text);
    } else {
      strength += 1;
    }
  });

  // Calculate strength percentage and text
  const strengthPercentage = (strength / requirements.length) * 100;
  let strengthText = 'Very Weak';
  
  if (strengthPercentage >= 80) strengthText = 'Very Strong';
  else if (strengthPercentage >= 60) strengthText = 'Strong';
  else if (strengthPercentage >= 40) strengthText = 'Medium';
  else if (strengthPercentage >= 20) strengthText = 'Weak';

  return {
    valid: errors.length === 0,
    errors,
    strength: strengthPercentage,
    strengthText,
    requirements
  };
};

// Confirm password validation
export const validateConfirmPassword = (password: string, confirmPassword: string): { valid: boolean; error?: string } => {
  if (!confirmPassword.trim()) {
    return { valid: false, error: 'Please confirm your password' };
  }
  
  if (password !== confirmPassword) {
    return { valid: false, error: 'Passwords do not match' };
  }
  
  return { valid: true };
};

// Sanitize input
export const sanitizeInput = (input: string): string => {
  return input.trim().replace(/[<>]/g, '');
};

// Real-time validation helpers
export const getEmailValidationState = (email: string): 'idle' | 'valid' | 'invalid' => {
  if (!email) return 'idle';
  const validation = validateEmail(email);
  return validation.valid ? 'valid' : 'invalid';
};

export const getUsernameValidationState = (username: string): 'idle' | 'valid' | 'invalid' => {
  if (!username) return 'idle';
  const validation = validateUsername(username);
  return validation.valid ? 'valid' : 'invalid';
};

export const getPasswordStrengthColor = (strength: number): string => {
  if (strength >= 80) return 'text-green-500';
  if (strength >= 60) return 'text-blue-500';
  if (strength >= 40) return 'text-yellow-500';
  if (strength >= 20) return 'text-orange-500';
  return 'text-red-500';
};

export const getPasswordStrengthBgColor = (strength: number): string => {
  if (strength >= 80) return 'bg-green-500';
  if (strength >= 60) return 'bg-blue-500';
  if (strength >= 40) return 'bg-yellow-500';
  if (strength >= 20) return 'bg-orange-500';
  return 'bg-red-500';
};
